import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import Notification from '../../components/Notification';
import Table from '../../components/Table';
import DeleteModal from '../../components/AlertModal';
import PageHeader from '../../components/PageHeader';
import Paginator from '../../components/Paginator';

import { Container, EmptyMessage } from './styles';

const itemLabels = {
  name: { label: 'Nome' },
  email: { label: 'E-mail' },
  cpf: { label: 'CPF' },
  verified: { label: 'Verificado' },
  type: { label: 'Tipo de usuário' },
  designerPremium: { label: 'Designer Premium' },
};

const Users = () => {
  const history = useHistory();
  const { loading } = useAuth();
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [isLoading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});

  const fetchUsers = useCallback(async requestedPage => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin/users', {
        params: { page: requestedPage },
      });

      setUsers(
        data.users.map(u => ({
          ...u,
          verified: u.verified ? 'Sim' : 'Não',
          designerPremium: u.designerPremium ? 'Sim' : 'Não',
        }))
      );
      setPagesCount(Math.max(Math.ceil(data.count / data.itemsPerPage), 1));
    } catch (err) {
      const error = err.response
        ? err.response.data.error
        : 'Ocorreu um erro ao buscar usuários';

      Notification({ message: error, type: Notification.types.ERROR });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchUsers(page);
    }
  }, [loading, page, fetchUsers]);

  function handleDeleteButtonClick(user) {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  }

  function handleDeleteModalClose() {
    setDeleteModalOpen(false);
    setTimeout(() => setSelectedUser({}), DeleteModal.closeTimeout);
  }

  async function handleDeleteModalConfirm() {
    try {
      setDeleteModalOpen(false);
      setLoading(true);
      await api.delete(`/admin/user/${selectedUser.id}`);
      Notification({
        message: 'Usuário excluído com sucesso',
        type: Notification.types.SUCCESS,
      });

      // Excluir o último da página deixaria uma página vazia depois do fim.
      const wasLastOfPage = users.length === 1 && page > 1;
      if (wasLastOfPage) setPage(page - 1);
      else await fetchUsers(page);
    } catch (err) {
      const error = err.response
        ? err.response.data.error
        : 'Ocorreu um erro ao excluir usuário';

      Notification({ message: error, type: Notification.types.ERROR });
      setLoading(false);
    }
  }

  function handleEditButtonClick(user) {
    history.push(`/user/${user.id}/edit`);
  }

  return (
    <>
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteModalConfirm}
        title={`Excluir ${selectedUser.name}`}
        text={`Deseja realmente excluir o usuário ${selectedUser.name}?`}
        type="delete"
      />
      <Container>
        <PageHeader title="Usuários" showAddButton={false} />
        {isLoading && <div className="loader" />}
        {!isLoading && users.length === 0 && (
          <EmptyMessage>Nenhum usuário encontrado.</EmptyMessage>
        )}
        {!isLoading && users.length > 0 && (
          <>
            <Table
              items={users}
              headers={itemLabels}
              onDelete={handleDeleteButtonClick}
              onEdit={handleEditButtonClick}
            />
            {pagesCount > 1 && (
              <Paginator
                pagesCount={pagesCount}
                forcePage={page}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default Users;
